import { cva, type VariantProps } from 'class-variance-authority';

import type { IconComponent } from '@/components/icons/types';
import { cn } from '@/utils/helpers/cn';

import type { SVGProps } from 'react';

/**
 * Único ponto de renderização de ícone. Padroniza tamanho e acessibilidade.
 *
 * Não conhece a biblioteca de ícones: aceita qualquer `IconComponent`, venha do
 * `registry.ts` ou dos ícones autorais em `components/icons/`. É o que permite
 * trocar a lib mexendo só no registro.
 *
 * Recebe o ícone **por prop**, e não por nome numa string: um registro
 * `{ flame: FireIcon, ... }` montado em runtime obrigaria a importar todos os
 * ícones para formar o mapa, e o bundle passaria a carregar o pacote inteiro em
 * vez dos ~26 que o app usa. Com `as`, cada tela importa só o que renderiza.
 *
 * O default de acessibilidade é `aria-hidden`, porque a esmagadora maioria dos
 * ícones acompanha um rótulo visível e repeti-lo no leitor de tela só polui.
 * Passe `label` quando o ícone for a **única** portadora do significado.
 *
 * @example
 * <Icon as={FireIcon} />                          // decorativo, ao lado de texto
 * <Icon as={FireIcon} size="lg" label="Incêndio" /> // sozinho, carrega o sentido
 */
export const iconVariants = cva('shrink-0', {
  variants: {
    size: {
      xs: 'size-3.5', // 14px — junto de caption
      sm: 'size-4', // 16px — junto de body
      md: 'size-5', // 20px — linha de lista, botão (default)
      lg: 'size-6', // 24px — navegação, cabeçalho
      xl: 'size-8', // 32px — estado vazio, destaque
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type IconProps = {
  /** O componente de ícone, vindo do registro: `as={FireIcon}`. */
  as: IconComponent;
  /**
   * Descrição para leitor de tela. Informe **apenas** quando o ícone carrega
   * significado sozinho — sem rótulo visível ao lado. Com `label` o ícone vira
   * `role="img"`; sem, some da árvore de acessibilidade.
   */
  label?: string;
} & VariantProps<typeof iconVariants> &
  // `size` sai porque o nativo do SVG é number|string e aqui é a escala do DS;
  // `color` sai porque a cor vem de `currentColor`, via `text-*` do tema.
  Omit<SVGProps<SVGSVGElement>, 'size' | 'color' | 'ref'>;

export function Icon({ as: Component, size, label, className, ...props }: IconProps) {
  return (
    <Component
      className={cn(iconVariants({ size }), className)}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      {...props}
    />
  );
}
